/**
 * French messages of the admin feature (commune-admin console). Merged into
 * the catalogue by `src/i18n/fr.ts` under the `admin` key, read everywhere
 * via `t('admin.…')`.
 */
export const adminMessages = {
  nav: {
    commune: 'Commune',
    inscriptions: 'Inscriptions & relances',
    notes: 'Revue des notes',
    engagements: 'Engagements',
    ariaLabel: 'Navigation de la console admin',
    backToApp: '← Retour à l’application',
  },
  refusal: {
    title: 'Console réservée aux admins de commune',
    body: "Cette page n'est accessible qu'aux personnes qui administrent une commune sur Dring. Si vous pensez que c'est une erreur, contactez l'admin de votre groupe d'action.",
    backHome: "Retour à l'accueil",
  },
  errors: {
    generic: 'Une erreur est survenue, réessayez dans un instant.',
  },
  commune: {
    title: 'Tableau de bord',
    lead: 'Où en est-on, que se passe-t-il maintenant, où va-t-on ensuite.',
    loadError: 'Impossible de charger le tableau de bord.',
    kpi: {
      doorsRecorded: 'Portes renseignées',
      doorsRecordedValue: '{{recorded}} / {{doors}}',
      openSessions: 'Sessions ouvertes',
      averageQuality: 'Qualité moyenne',
      noQuality: '—',
      notesToReview: 'Notes signalées',
      casesWithoutEmail: 'Dossiers sans e-mail',
      followupsFailed: 'Relances en échec',
    },
    map: {
      kicker: 'Sessions en cours',
      lead: 'Carte des sessions ouvertes, pour choisir où renforcer.',
      mapLabel: 'Carte des sessions ouvertes',
      empty: 'Aucune session ouverte en ce moment.',
      participants: '{{count}} participant·es',
      participant: '1 participant·e',
      quality: 'Qualité {{value}} ★',
      noQuality: 'Qualité : pas encore de visite notée',
      progress: '{{recorded}} renseignées · {{assigned}} en cours · {{remaining}} restantes',
      kindStandard: 'PAP standard',
      kindRegistration: 'Spécial inscription',
    },
    coverage: {
      kicker: 'Couverture par rue',
      lead: 'La rue avec le plus de portes cibles restantes en premier.',
      street: 'Rue',
      doors: 'Portes',
      recorded: 'Renseignées',
      targets: 'Cibles PAP',
      share: 'Couverture',
      empty: 'Aucune rue à afficher pour le moment.',
    },
    referents: {
      kicker: 'Bâtiments avec référent·e',
      summary: '{{withReferent}} sur {{total}}',
      building: 'Bâtiment',
      doors: 'Portes',
      households: 'Foyers connus',
      status: 'Référent·e',
      has: 'Oui',
      missing: 'Non',
      empty: 'Aucun bâtiment recensé pour le moment.',
    },
  },
  inscriptions: {
    title: 'Inscriptions & relances',
    lead: 'Le tunnel des inscriptions avance-t-il, qu’est-ce qui coince.',
    loadError: 'Impossible de charger les dossiers.',
    funnel: {
      kicker: 'Le tunnel',
      infoGiven: 'Infos remises',
      filed: 'Dossiers déposés',
      confirmed: 'Confirmés au REU',
      closed: 'Dossiers clos',
    },
    sequenceFunnel: {
      kicker: 'La séquence de relance',
      lead: 'Les mails partent seuls (J+2, J+7, J+14), avec un lien de désabonnement en un clic.',
      sent7d: 'Mails envoyés (7 jours)',
      scheduled: 'Envois programmés',
      replied: 'Réponses',
      unsubscribed: 'Désabonnements',
      failed: 'Envois en échec',
      noOpenRate:
        'Nous ne mesurons pas les ouvertures : cela demanderait une image invisible qui prévient nos serveurs à chaque lecture. Une « réponse » est un clic sur un des boutons du mail.',
    },
    empty: 'Aucun dossier pour le moment.',
    table: {
      firstName: 'Prénom',
      door: 'Porte',
      status: 'Étape',
      openedAt: 'Ouvert le',
      consentAt: 'Consentement',
      volunteer: 'Collecté par',
      sequence: 'Relances',
    },
    status: {
      info_given: 'Information donnée',
      filed: 'Dossier déposé',
      confirmed: 'Confirmé',
      closed: 'Clos',
    },
    sequence: {
      none: 'Aucune — pas d’e-mail',
      completed: 'Séquence terminée · sans réponse',
      running: 'En cours',
      stopped: 'Arrêtées',
      replied: 'A répondu',
      unsubscribed: 'Désinscrit·e',
      failed: 'Échec à réarmer',
    },
    step: {
      j2: 'J+2',
      j7: 'J+7',
      j14: 'J+14',
    },
    nextScheduled: 'Prochaine relance {{step}} · {{date}}',
    lastSent: 'Dernière envoyée {{step}} · {{date}}',
    smsGateway: 'Passerelle SMS à brancher.',
  },
  notes: {
    title: 'Revue des notes',
    lead: 'Ce qui doit être purgé cette semaine.',
    loadError: 'Impossible de charger les notes.',
    empty: 'Aucune note à relire.',
    filterLabel: 'Filtrer',
    filter: {
      all: 'Toutes',
      flagged: 'Signalées',
      unflagged: 'Non signalées',
    },
    reason: 'Signalée · {{reason}}',
    recordedAt: 'Renseignée le {{date}} par {{author}}',
    purge: 'Purger',
    purgeConfirmTitle: 'Purger cette note ?',
    purgeConfirmBody:
      'Le texte de la note sera définitivement effacé. Cette action ne peut pas être annulée.',
    purgeConfirmAction: 'Purger définitivement',
    purgeSuccess: 'Note purgée.',
    keep: 'Garder',
    keepSuccess: 'Note conservée, retirée de la file signalée.',
    actionError: "L'action a échoué, réessayez.",
  },
  engagements: {
    title: 'Engagements',
    lead: 'Qui a demandé quoi, et comment le transmettre aux bons outils.',
    loadError: 'Impossible de charger les engagements.',
    countsKicker: 'Par type d’engagement',
    countLabel: '{{count}} personne',
    countLabelPlural: '{{count}} personnes',
    referentsKicker: 'Bâtiments encore sans référent·e',
    referentsLead:
      '{{missing}} bâtiment sur {{total}} sans référent·e — à proposer en priorité lors des prochains passages.',
    referentsLeadPlural:
      '{{missing}} bâtiments sur {{total}} sans référent·e — à proposer en priorité lors des prochains passages.',
    referentsEmpty: 'Tous les bâtiments recensés ont un·e référent·e.',
    exportKicker: 'Export CSV',
    exportLead:
      'Ce fichier ne doit être remis qu’à l’outil cible correspondant à cet engagement (outil de newsletter, plateforme de campagne, groupe d’action…). La structure qui le reçoit en devient responsable au sens du RGPD.',
    kindSelectLabel: 'Type à exporter',
    includeExportedLabel: 'Inclure les contacts déjà exportés',
    exportButton: 'Télécharger le CSV',
    exportButtonPending: 'Préparation du fichier…',
    exportSuccess: '{{count}} contact·s exporté·s.',
    exportEmpty: 'Aucun contact à exporter pour ce type.',
    exportError: "L'export a échoué, réessayez.",
  },
} as const;
